/* ScanIntake.jsx — 5-vragen wizard tussen homepage en gratis scan
   Route: /scan-intake?token=...
   i18n: alle strings via `scan_intake.*` namespace (7 talen).
*/

import { useEffect, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import {
  ArrowRight, ArrowLeft, Loader2, Building2, Calendar, Ruler, Users, Receipt,
} from "lucide-react";
import { apiClient } from "../lib/api";
import LanguageSwitcher from "../components/LanguageSwitcher";

const HOUSING_KEYS = [
  { value: "appartement", icon: "🏢" },
  { value: "rijwoning", icon: "🏘️" },
  { value: "vrijstaand", icon: "🏡" },
  { value: "villa", icon: "🏛️" },
];

export default function ScanIntake() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const token = params.get("token");
  const nav = useNavigate();

  const [lead, setLead] = useState(null);
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);

  const [housingType, setHousingType] = useState("");
  const [year, setYear] = useState("");
  const [m2, setM2] = useState("");
  const [persons, setPersons] = useState("");
  const [bill, setBill] = useState("");

  useEffect(() => {
    if (!token) { nav("/"); return; }
    apiClient.get("/leads/me", { params: { token } })
      .then((r) => setLead(r.data))
      .catch(() => { toast.error(t("scan_intake.token_invalid")); setTimeout(() => nav("/"), 1500); });
  }, [token, nav, t]);

  const TOTAL = 5;
  const pct = (step / TOTAL) * 100;

  const next = () => setStep((s) => Math.min(TOTAL, s + 1));
  const back = () => setStep((s) => Math.max(1, s - 1));

  const canNext = () => {
    if (step === 1) return !!housingType;
    if (step === 2) return year && Number(year) >= 1900 && Number(year) <= 2030;
    if (step === 3) return m2 && Number(m2) >= 20 && Number(m2) <= 2000;
    if (step === 4) return persons && Number(persons) >= 1 && Number(persons) <= 20;
    if (step === 5) return bill && Number(bill) >= 20 && Number(bill) <= 2000;
    return false;
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      await apiClient.post(`/intake/scan?token=${token}`, {
        housing_type: housingType,
        construction_year: Number(year),
        m2: Number(m2),
        persons: Number(persons),
        monthly_bill_eur: Number(bill),
      });
      toast.success(t("scan_intake.submit_success"));
      nav(`/scan?token=${token}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("scan_intake.submit_error"));
      setSubmitting(false);
    }
  };

  if (!lead) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <Loader2 className="w-10 h-10 text-[#FCBF49] animate-spin" />
      </div>
    );
  }

  const presets2 = t("scan_intake.s2_presets", { returnObjects: true }) || [];

  return (
    <div className="min-h-screen bg-[#F4F7FA] text-[#003049] font-sans">
      <header className="bg-white border-b border-[#E5E9EE]">
        <div className="max-w-3xl mx-auto px-6 lg:px-10 h-[64px] flex items-center justify-between">
          <Link to="/" className="flex items-center">
            <img src="/img/doctorsol-logo.svg" alt="DoctorSol" className="h-12 w-auto" draggable={false} />
          </Link>
          <div className="flex items-center gap-3">
            <div className="text-xs font-semibold text-[#52606E] tabular-nums">
              {t("scan_intake.header_q", { step, total: TOTAL })}
            </div>
            <LanguageSwitcher />
          </div>
        </div>
        <div className="h-1 bg-[#EFF2F5]">
          <div className="h-full bg-[#FCBF49] transition-all duration-300" style={{ width: `${pct}%` }} data-testid="intake-progress" />
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-6 lg:px-10 py-12 lg:py-16">
        <div className="bg-white rounded-2xl p-8 lg:p-12 shadow-sm border border-[#EFF2F5]">

          {step === 1 && (
            <Step icon={Building2} title={t("scan_intake.s1_title")} subtitle={t("scan_intake.s1_sub")}>
              <div className="grid grid-cols-2 gap-3">
                {HOUSING_KEYS.map((h) => (
                  <button key={h.value} data-testid={`type-${h.value}`}
                    onClick={() => setHousingType(h.value)}
                    className={`text-left rounded-xl border-2 px-5 py-5 transition-all ${
                      housingType === h.value
                        ? "border-[#FCBF49] bg-[#FFFBEF]"
                        : "border-[#EFF2F5] hover:border-[#FCBF49]/40 bg-white"
                    }`}>
                    <div className="text-3xl mb-2">{h.icon}</div>
                    <div className="font-bold text-[#003049]">{t(`scan_intake.type.label_${h.value}`)}</div>
                  </button>
                ))}
              </div>
            </Step>
          )}

          {step === 2 && (
            <Step icon={Calendar} title={t("scan_intake.s2_title")} subtitle={t("scan_intake.s2_sub")}>
              <Field value={year} onChange={setYear} type="number" placeholder={t("scan_intake.s2_placeholder")}
                testid="field-year" min="1900" max="2030" suffix="" />
              <div className="flex flex-wrap gap-2 mt-3">
                {presets2.map((preset, i) => {
                  const vals = [1970, 1990, 2005, 2015, 2022];
                  return (
                    <button key={preset} type="button" onClick={() => setYear(vals[i])}
                      className="text-xs px-3 py-1.5 rounded-full bg-[#F7F8FA] hover:bg-[#FFFBEF] text-[#52606E] hover:text-[#003049] transition">
                      {preset}
                    </button>
                  );
                })}
              </div>
            </Step>
          )}

          {step === 3 && (
            <Step icon={Ruler} title={t("scan_intake.s3_title")} subtitle={t("scan_intake.s3_sub")}>
              <Field value={m2} onChange={setM2} type="number" placeholder={t("scan_intake.s3_placeholder")}
                testid="field-m2" min="20" max="2000" suffix="m²" />
            </Step>
          )}

          {step === 4 && (
            <Step icon={Users} title={t("scan_intake.s4_title")} subtitle={t("scan_intake.s4_sub")}>
              <div className="grid grid-cols-5 gap-2">
                {[1, 2, 3, 4, 5].map((n) => (
                  <button key={n} data-testid={`persons-${n}`}
                    onClick={() => setPersons(n)}
                    className={`rounded-xl border-2 py-4 font-bold text-2xl transition-all ${
                      Number(persons) === n
                        ? "border-[#FCBF49] bg-[#FFFBEF] text-[#003049]"
                        : "border-[#EFF2F5] hover:border-[#FCBF49]/40 text-[#52606E] bg-white"
                    }`}>
                    {n}
                  </button>
                ))}
              </div>
              <div className="mt-4">
                <Field value={persons > 5 ? persons : ""} onChange={setPersons} type="number"
                  placeholder={t("scan_intake.s4_other")} testid="field-persons-other" min="6" max="20" />
              </div>
            </Step>
          )}

          {step === 5 && (
            <Step icon={Receipt} title={t("scan_intake.s5_title")} subtitle={t("scan_intake.s5_sub")}>
              <Field value={bill} onChange={setBill} type="number" placeholder={t("scan_intake.s5_placeholder")}
                testid="field-bill" min="20" max="2000" suffix="€" />
              <div className="flex flex-wrap gap-2 mt-3">
                {[80, 120, 180, 250, 350].map((v) => (
                  <button key={v} type="button" onClick={() => setBill(v)}
                    className="text-xs px-3 py-1.5 rounded-full bg-[#F7F8FA] hover:bg-[#FFFBEF] text-[#52606E] hover:text-[#003049] transition">
                    € {v}
                  </button>
                ))}
              </div>
            </Step>
          )}

          <div className="mt-10 flex items-center justify-between gap-3">
            {step > 1 ? (
              <button onClick={back} data-testid="intake-back"
                className="inline-flex items-center gap-2 px-5 py-3 text-[#52606E] hover:text-[#003049] font-medium">
                <ArrowLeft className="w-4 h-4" /> {t("scan_intake.back")}
              </button>
            ) : <div />}

            {step < TOTAL ? (
              <button onClick={next} disabled={!canNext()} data-testid="intake-next"
                className="inline-flex items-center gap-2 bg-[#FCBF49] hover:bg-[#E5AB2F] text-[#003049] font-bold rounded-md px-7 py-3 transition disabled:opacity-50 disabled:cursor-not-allowed">
                {t("scan_intake.next")} <ArrowRight className="w-4 h-4" />
              </button>
            ) : (
              <button onClick={submit} disabled={!canNext() || submitting} data-testid="intake-submit"
                className="inline-flex items-center gap-2 bg-[#FCBF49] hover:bg-[#E5AB2F] text-[#003049] font-bold rounded-md px-7 py-3 transition disabled:opacity-50">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <>{t("scan_intake.submit")} <ArrowRight className="w-4 h-4" /></>}
              </button>
            )}
          </div>
        </div>

        <p className="text-center text-xs text-[#52606E] mt-6 max-w-md mx-auto">
          {t("scan_intake.footer")}
        </p>
      </main>
    </div>
  );
}

function Step({ icon: Icon, title, subtitle, children }) {
  return (
    <div data-testid="intake-step">
      <div className="w-12 h-12 rounded-xl bg-[#FCBF49]/15 inline-flex items-center justify-center mb-5">
        <Icon className="w-6 h-6 text-[#FCBF49]" />
      </div>
      <h1 className="text-2xl sm:text-3xl font-bold text-[#003049] leading-tight mb-2">{title}</h1>
      <p className="text-[#52606E] mb-7 leading-relaxed">{subtitle}</p>
      {children}
    </div>
  );
}

function Field({ value, onChange, suffix, testid, ...rest }) {
  return (
    <div className="relative">
      <input {...rest} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
        className="w-full bg-[#F7F8FA] border-2 border-[#E5E9EE] rounded-xl px-5 py-4 text-lg outline-none focus:border-[#FCBF49] focus:ring-4 focus:ring-[#FCBF49]/20 transition" />
      {suffix && <span className="absolute right-5 top-1/2 -translate-y-1/2 text-[#52606E] font-medium">{suffix}</span>}
    </div>
  );
}
